import { Button, Checkbox, Grid, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardGrid } from 'src/components/cardGrid';
import { FilterList } from 'src/components/filterList';
import { Footer } from 'src/components/footer/Footer';
import { Header } from 'src/components/header';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { SimpleDialog } from 'src/components/modal';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress';
import { Snackbar } from 'src/components/snackbar';
import { StyledTypography } from 'src/components/subHeader/SubHeaderWithSorting.styled';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting, useModal } from 'src/hooks';
import { IAssembly, IFilter, IStatus, IStatusLabel } from 'src/interfaces';
import { DataTestID, lang, useUserRole } from 'src/utils';

import {
  AssemblyContainer,
  AssemblySectionHeader,
  CardGridWrapper,
  CheckboxLabel,
  InputContainer,
  InputTextField,
  StyleDivider,
} from './AddAssemblyTagPage.styled';
import { ALLOWED_REGEX, tagNameMaxLength, TestIds } from './constants';
import { useCreateAssemblyTagPage } from './hooks/AddAssemblyTagPage.hook';

export const AddAssemblyTagPage = () => {
  const [inputBoxStatus, setInputBoxStatus] = React.useState('');
  const [assemblyTagName, setAssemblyTagName] = React.useState('');
  const [selectedAssemblies, setSelectedAssemblies] = React.useState<
    IAssembly[]
  >([]);
  const gridBottomRef = useRef<HTMLDivElement>(null);
  const [selectAll, setSelectAll] = React.useState(false);

  const {
    assembliesData,
    isLoadingAssemblies,
    getNextAssemblyPage,
    errorLoadingAssemblies,
    hasNextPage,
    fetchNextPage,
    onChangeParams,
    createAssemblyTag,
    updateAssembly,
  } = useCreateAssemblyTagPage();

  const totalCount = assembliesData?.pages?.[0]?.totalCount || 0;

  const numberofResults =
    assembliesData?.pages?.reduce((total, page) => {
      return total + (page.assemblies?.length || 0);
    }, 0) || 0;

  const shouldDisplayResultInfo = numberofResults != null && totalCount != null;
  const resultCountText = `${lang.Showing} ${numberofResults}/${totalCount}`;

  const navigate = useNavigate();
  const CloseModal = useModal();
  const { isViewOnly } = useUserRole();

  const assemblies = useMemo(() => {
    return assembliesData?.pages.flatMap((page) => page.assemblies) || [];
  }, [assembliesData]);

  const selectedCount = selectedAssemblies.length;

  useEffect(() => {
    if (assemblies.length && gridBottomRef.current) {
      const { bottom } = gridBottomRef.current.getBoundingClientRect();
      const shouldInstantlyFetchNextPage =
        bottom < window.innerHeight && hasNextPage;

      if (shouldInstantlyFetchNextPage) {
        fetchNextPage();
      }
      setSelectAll(areAllAssembliesSelected(assemblies, selectedAssemblies));
    }
  }, [assemblies, fetchNextPage, hasNextPage, selectedAssemblies]);

  const filters: IFilter[] = [
    {
      label: 'Status',
      options: Object.values(IStatus).map((value) => ({
        value: value,
        label: IStatusLabel[`${value}`],
      })),
    },
    {
      label: lang.assemblyTags,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'primary',
      },
    },
  ];

  const { onChangeKeyword, onRemoveKeyword, onReset } =
    useFilterAndSorting(filters);

  const handleSelectAllClick = () => {
    if (selectAll) {
      setSelectedAssemblies([]);
    } else {
      setSelectedAssemblies(assemblies);
    }
    setSelectAll(!selectAll);
  };

  const handleAssemblyTagNameChange = (value: string) => {
    if (value.length > 100) {
      setInputBoxStatus(lang.characterLimit);
      setAssemblyTagName(value);

      return;
    }
    ALLOWED_REGEX.test(value)
      ? setInputBoxStatus('')
      : setInputBoxStatus(lang.invalidTagName);
    setAssemblyTagName(value);
  };

  const handleAssemblyCheckboxChange = useCallback(
    (assembly: IAssembly) => {
      const isSelected = selectedAssemblies.some(
        (selectedAssembly) => selectedAssembly.id === assembly.id,
      );

      const updatedSelectedAssemblies = isSelected
        ? selectedAssemblies.filter(
            (selectedAssembly) => selectedAssembly.id !== assembly.id,
          )
        : [...selectedAssemblies, assembly];

      setSelectedAssemblies(updatedSelectedAssemblies);
      setSelectAll(
        areAllAssembliesSelected(assemblies, updatedSelectedAssemblies),
      );
    },
    [assemblies, setSelectedAssemblies, selectedAssemblies, setSelectAll],
  );

  const areAllAssembliesSelected = (
    assemblies: IAssembly[],
    selected: IAssembly[],
  ) => {
    return assemblies.every((assemblies) =>
      selected.some(
        (selectedAssembly) => selectedAssembly.id === assemblies.id,
      ),
    );
  };
  const handleModalConfirm = () => {
    navigate(PATHS.ASSEMBLY_MANAGE_TAGS);
  };

  const handleDone = async () => {
    try {
      if (assemblyTagName) {
        const request = {
          name: assemblyTagName,
        };
        const result = await createAssemblyTag.mutateAsync(request);
        if (result.id) {
          const updatedAssemblies = selectedAssemblies.map((assembly) => ({
            ...assembly,
            assembly_tag_id: [
              ...(assembly.assembly_tag_id || []),
              Number(result.id),
            ],
          }));
          await updateAssembly.mutateAsync(updatedAssemblies);
          navigate(PATHS.ASSEMBLY_MANAGE_TAGS);
          Snackbar.success(`${assemblyTagName} ${lang.successfullyCreated}`);
        }
      }
    } catch (error: any) {
      if (
        error.response?.data?.error?.includes(
          'An item with the same unique field already exists',
        )
      ) {
        setInputBoxStatus(lang.uniqueTagAlert);
      }
    }
  };

  return (
    <Grid>
      <Header
        backLink={-1}
        title={lang.createNewAssemblyTag}
        breadcrumbItems={[
          {
            label: lang.assemblies,
            link: `${PATHS.ASSEMBLIES}`,
          },
          {
            label: lang.tags,
            link: `${PATHS.ASSEMBLY_MANAGE_TAGS}`,
          },
          {
            label: lang.createTag,
          },
        ]}
        actionBar={isViewOnly && <ViewOnlyAlert />}
      />
      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <FilterList
            filters={filters}
            onChangeKeyword={onChangeKeyword}
            onRemoveKeyword={onRemoveKeyword}
            onChangeFilter={onChangeParams}
            showReset
            onReset={onReset}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <Typography variant="h4" component="h2" gutterBottom>
            Create tag
          </Typography>
          <StyleDivider />
          <InputContainer>
            <InputTextLabel>{lang.tagName}</InputTextLabel>
            <InputTextField
              variant="outlined"
              inputProps={{
                maxLength: tagNameMaxLength,
                'data-testid': TestIds.TAG_NAME_INPUT,
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleAssemblyTagNameChange(event.target.value)
              }
              onBlur={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleAssemblyTagNameChange(event.target.value.trim())
              }
              value={assemblyTagName}
              error={!!inputBoxStatus}
              helperText={inputBoxStatus}
            />
          </InputContainer>
          {shouldDisplayResultInfo && (
            <StyledTypography paddingTop={4}>
              {resultCountText}
            </StyledTypography>
          )}

          <AssemblyContainer>
            {assemblies.length > 0 && (
              <AssemblySectionHeader>
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  flexGrow={1}>
                  {lang.selectAssembly}
                </Typography>
                <Typography
                  variant="body1"
                  color="grey"
                  marginRight={1}
                  marginBottom={0}
                  paddingTop={1}>
                  {`${selectedCount} Selected`}
                </Typography>
                <Button variant="text" onClick={handleSelectAllClick}>
                  {selectAll ? lang.deselectAll : lang.selectAll}
                </Button>
              </AssemblySectionHeader>
            )}
            <StyleDivider />
            {isLoadingAssemblies ? (
              <Progress />
            ) : (
              <CardGridWrapper>
                <NoResultAlert
                  isLoading={isLoadingAssemblies}
                  dataToCheck={assemblies}
                />
                <CardGrid
                  isLoading={isLoadingAssemblies}
                  error={errorLoadingAssemblies}
                  updatePage={getNextAssemblyPage}>
                  {assemblies.map((assembly: IAssembly) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      key={assembly.id}
                      data-testid={DataTestID.AddAssemblyTagAssemblyData}>
                      <CheckboxLabel
                        label={`${assembly.name}`}
                        control={
                          <Checkbox
                            checked={selectedAssemblies.some(
                              (selectedAssemblies) =>
                                selectedAssemblies.id === assembly.id,
                            )}
                            onChange={() =>
                              handleAssemblyCheckboxChange(assembly)
                            }
                          />
                        }
                      />
                    </Grid>
                  ))}
                </CardGrid>
                <div ref={gridBottomRef}></div>
              </CardGridWrapper>
            )}
          </AssemblyContainer>
        </Grid>
      </Grid>
      <SimpleDialog
        open={CloseModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.discardModal.title}
        onClose={CloseModal.onCloseModal}
        onClickCancel={CloseModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleModalConfirm}
      />
      {!isViewOnly && (
        <Footer
          showFooterActions
          onCancel={CloseModal.onOpenModal}
          onSave={handleDone}
          cancelLabel={lang.close}
          saveLabel={lang.create}
          disableSave={
            assemblyTagName === '' || inputBoxStatus !== ''
          }></Footer>
      )}
    </Grid>
  );
};
